import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { healthApi } from '@/api/healthApi';

/**
 * Calm narrative card describing the user's last 7 days of activity.
 *
 * Reads from /api/streak/story which returns a short, human sentence like:
 *   "You've shown up a few times this week."
 * No numbers in the headline; the small footer line shows the soft shape
 * (perfect / active / rested days) for users who want it, but never as
 * a scoreboard.
 */
const StreakStory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    healthApi
      .getStreakStory()
      .then((res) => {
        if (!cancelled) setData(res?.data || null);
      })
      .catch(() => {
        // Silent — this card is purely supportive copy. If the endpoint
        // fails, we just don't render the card.
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="border border-emerald-100 bg-emerald-50/60 rounded-2xl p-5 flex items-center gap-3 shadow-sm animate-pulse"
        data-testid="streak-story-loading"
      >
        <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
        <p className="text-sm text-emerald-900/60">Looking at your week…</p>
      </div>
    );
  }

  if (!data?.story) return null;

  const shape = data.shape || {};
  const perfect = shape.perfect_days ?? 0;
  const rest = shape.rest_days ?? 0;

  return (
    <div
      className="border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 shadow-sm"
      data-testid="streak-story-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1.5">
            Your week so far
          </p>
          <p
            className="text-base text-slate-900 leading-relaxed font-medium"
            data-testid="streak-story-text"
          >
            {data.story}
          </p>
          {(perfect > 0 || rest > 0) && (
            <p
              className="text-xs text-slate-500 mt-2"
              data-testid="streak-story-shape"
            >
              {perfect > 0 && (
                <>
                  {perfect} day{perfect === 1 ? '' : 's'} of showing up
                </>
              )}
              {perfect > 0 && rest > 0 && ' · '}
              {rest > 0 && (
                <>
                  {rest} gentle rest day{rest === 1 ? '' : 's'}
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreakStory;
